import { Link } from "react-router-dom";
import {
  Mail, Phone, MapPin, ArrowRight, MessageCircle, Clock, CheckCircle2, CalendarCheck,
} from "lucide-react";
import {
  Ornament, Reveal, Kicker, Titel, PrimaryBtn, GhostBtn,
} from "@/components/vorschau/Bausteine";

const WA = "https://wa.me/4915127282560?text=" + encodeURIComponent(
  "Guten Tag Herr Stutz, ich habe eine Anfrage: ");

const WEGE = [
  {
    icon: MessageCircle, label: "WhatsApp", wert: "Direkt schreiben",
    zusatz: "Der schnellste Weg — meist Antwort am selben Tag.",
    href: WA, testid: "vorschau-kontakt-whatsapp", extern: true,
  },
  {
    icon: Phone, label: "Telefon", wert: "+49 151 27282560",
    zusatz: "Auch abends und am Wochenende, zwischen den Einsätzen.",
    href: "tel:+4915127282560", testid: "vorschau-kontakt-phone",
  },
  {
    icon: Mail, label: "E-Mail", wert: "mietkoch@stutz-cuisine.com",
    zusatz: "Für Angebote, Rechnungen und alles Schriftliche.",
    href: "mailto:mietkoch@stutz-cuisine.com", testid: "vorschau-kontakt-email",
  },
];

const ABLAUF = [
  ["Sie schreiben kurz", "Anlass, Datum, Gästezahl und wo es stattfindet — mehr brauche ich nicht."],
  ["Ich melde mich", "Meist innerhalb von 24 Stunden, mit einer ehrlichen Antwort, ob es passt."],
  ["Sie bekommen Ihr Angebot", "Als eigene Seite mit Menü und Preis — annehmen können Sie mit einem Klick."],
];

const GEBIET = [
  "Michelstadt & Odenwaldkreis", "Erbach, Bad König, Höchst",
  "Darmstadt & Rhein-Main", "Aschaffenburg & Miltenberg",
  "Heidelberg & Bergstraße", "Weiter entfernt auf Anfrage",
];

export default function KontaktVorschau() {
  return (
    <div data-testid="page-vorschau-kontakt" className="bg-[#FDF8F1] text-[#2B2320] selection:bg-[#BA6D52] selection:text-white">
      {/* Hero */}
      <section className="relative min-h-[62vh] overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-cover bg-center animate-kenburns"
          style={{ backgroundImage: "url('/event-bg-gericht.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(18,16,12,0.74)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#FDF8F1]" />
        <div className="relative w-full max-w-4xl mx-auto px-6 py-20 text-center">
          <img src="/logo.png" alt="Lukas Stutz"
            className="h-16 w-16 mx-auto rounded-full bg-[#FAF8F3]/90 p-1.5 animate-in fade-in zoom-in-90 duration-1000" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2C4B2] mt-6 animate-in fade-in duration-1000 delay-150">
            Kontakt
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#FFFBF4] mt-6 drop-shadow-[0_3px_18px_rgba(0,0,0,0.9)] animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-200">
            Kein Callcenter.<br className="hidden sm:block" /> Nur ich.
          </h1>
          <p className="max-w-lg mx-auto text-base md:text-lg text-[#F2E7DA] mt-7 font-light leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)] animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
            Sie erreichen immer denselben Menschen, der später auch bei Ihnen am Herd steht.
          </p>
        </div>
      </section>

      {/* Kontaktwege */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {WEGE.map((w, i) => (
            <Reveal key={w.label} delay={i * 120}>
              <a href={w.href} data-testid={w.testid}
                {...(w.extern ? { target: "_blank", rel: "noreferrer" } : {})}
                className="group block h-full bg-[#FFFDF8] border border-[#E6D8C2] hover:border-[#BA6D52] hover:-translate-y-1 hover:shadow-[0_26px_50px_-34px_rgba(120,80,48,0.6)] transition-all duration-500 p-7">
                <w.icon size={24} strokeWidth={1.3} className="text-[#BA6D52]" />
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#A08C7B] mt-6">{w.label}</p>
                <p className="font-serif text-xl sm:text-2xl mt-3 break-words group-hover:text-[#BA6D52] transition-colors">
                  {w.wert}
                </p>
                <p className="text-sm text-[#6B5C50] mt-3 font-light leading-relaxed">{w.zusatz}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] opacity-0 group-hover:opacity-100 transition-opacity">
                  Öffnen <ArrowRight size={13} />
                </span>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200} className="mt-10">
          <div className="border border-[#E6D8C2] bg-[#F7EADA] px-6 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
            {[[Clock, "Antwort meist in 24 Stunden"], [CheckCircle2, "Kurzfristige Einsätze möglich"],
              [CalendarCheck, "Termine bis 12 Monate im Voraus"]].map(([Icon, text]) => (
              <span key={text} className="inline-flex items-center gap-2.5 text-xs text-[#5A4D43]">
                <Icon size={14} className="text-[#BA6D52]" /> {text}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Ablauf */}
      <section className="bg-[#FFFDF8] border-y border-[#EFE3D2]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 sm:py-28">
          <Reveal className="text-center">
            <Kicker>So läuft es ab</Kicker>
            <Titel className="mt-5">Drei Schritte, keine Formulare-Schlacht.</Titel>
            <Ornament className="mt-8" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {ABLAUF.map(([titel, text], i) => (
              <Reveal key={titel} delay={i * 140} testid={`vorschau-ablauf-${i}`}>
                <span className="font-serif text-4xl font-light text-[#E0D3C0]">0{i + 1}</span>
                <p className="font-serif text-xl mt-4">{titel}</p>
                <span className="block w-10 h-px bg-[#BA6D52] mt-3" />
                <p className="text-sm text-[#6B5C50] mt-4 font-light leading-relaxed">{text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200} className="text-center mt-16">
            <PrimaryBtn href="/anfrage-betrieb" data-testid="vorschau-kontakt-cta">
              Anfrage starten <ArrowRight size={15} />
            </PrimaryBtn>
          </Reveal>
        </div>
      </section>

      {/* Einsatzgebiet */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 sm:py-28 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <MapPin size={22} className="text-[#BA6D52]" />
          <Titel className="mt-5">Wo ich koche.</Titel>
          <p className="text-sm text-[#6B5C50] mt-6 font-light leading-relaxed">
            Zuhause bin ich in Michelstadt im Odenwald. Von dort fahre ich in die ganze Region —
            die Anfahrt stimmen wir vorab ab und ich weise sie im Angebot transparent aus.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-9">
            {GEBIET.map((g) => (
              <li key={g} className="flex items-start gap-2.5 text-sm text-[#5A4D43]">
                <CheckCircle2 size={15} className="text-[#BA6D52] shrink-0 mt-0.5" /> {g}
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <GhostBtn href="/vorschau/start" data-testid="vorschau-kontakt-zur-start">
              Zur Startseite
            </GhostBtn>
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="bg-[#FFFDF8] border border-[#E6D8C2] p-2.5">
            <div className="aspect-[4/3] overflow-hidden">
              <img src="/event-kueche.jpg" alt="Lukas Stutz in der Küche" loading="lazy"
                data-testid="vorschau-kontakt-foto"
                className="w-full h-full object-cover" />
            </div>
            <p className="px-2 pt-4 pb-2 text-xs text-[#8B7A68] italic">
              Zwischen zwei Gängen — meistens erreiche ich das Telefon trotzdem.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Abschluss */}
      <section className="relative overflow-hidden text-[#FAF3E8]">
        <div className="absolute inset-0 bg-cover bg-center animate-kenburns"
          style={{ backgroundImage: "url('/event-bg-dessert.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,13,10,0.78)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-20 sm:py-28 text-center">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light leading-tight">
              Schreiben Sie einfach los —<br className="hidden sm:block" /> ich beiße nicht.
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
              <PrimaryBtn href={WA} target="_blank" rel="noreferrer"
                data-testid="vorschau-kontakt-final-wa">
                <MessageCircle size={15} /> Über WhatsApp schreiben
              </PrimaryBtn>
              <a href="tel:+4915127282560" data-testid="vorschau-kontakt-final-call"
                className="inline-flex items-center justify-center gap-2.5 border border-white/40 text-[#FAF3E8] hover:bg-white/10 px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors">
                <Phone size={15} /> Anrufen
              </a>
            </div>
            <p className="text-[11px] text-[#A89D91] mt-14">
              Lukas Stutz · Schloßstraße 32 · 64720 Michelstadt ·{" "}
              <Link to="/impressum" className="hover:text-[#BA6D52] transition-colors">Impressum</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
